import React, { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppliance } from '@/lib/useAppliance';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiLoading, mdiPencil, mdiPlus, mdiTrashCan } from '@mdi/js';
import type { ApplianceDTO, RoutingRedirectDTO } from '@webip/shared/src/dto/appliance';
import { RoutingRedirectModal } from './RoutingRedirectModal';
import { extractApiError } from '@/lib/apiError';

const Page = () => {
    const { id: paramId } = useParams<{ id: string }>();
    const { applianceId } = useAppliance();
    const id = paramId ?? applianceId ?? undefined;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<RoutingRedirectDTO | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const applianceQuery = useQuery({
        queryKey: ['appliance', id],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: ApplianceDTO }>(`/api/appliance/${id}`);
            return res.data.result;
        },
        enabled: !!id,
    });

    const redirectsQuery = useQuery({
        queryKey: ['routing-redirect', id],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: RoutingRedirectDTO[] }>(
                `/api/routing-redirect?appliance_id=${id}`
            );
            return res.data.result;
        },
        enabled: !!id,
    });

    const mutCreate = useMutation({
        mutationFn: async (data: Omit<RoutingRedirectDTO, 'id' | 'wan_nome' | 'data_criacao' | 'data_ultima_atualizacao'>) => {
            return await axios.post('/api/routing-redirect', data);
        },
        onSuccess: () => {
            toastSuccess('Redirecionamento criado com sucesso!');
            queryClient.invalidateQueries(['routing-redirect', id]);
            handleCloseModal();
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao criar redirecionamento');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutUpdate = useMutation({
        mutationFn: async ({ redirectId, data }: { redirectId: number; data: Partial<RoutingRedirectDTO> }) => {
            return await axios.put(`/api/routing-redirect/${redirectId}`, data);
        },
        onSuccess: () => {
            toastSuccess('Redirecionamento atualizado com sucesso!');
            queryClient.invalidateQueries(['routing-redirect', id]);
            handleCloseModal();
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao atualizar redirecionamento');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutDelete = useMutation({
        mutationFn: async (redirectId: number) => {
            return await axios.delete(`/api/routing-redirect/${redirectId}`);
        },
        onSuccess: () => {
            toastSuccess('Redirecionamento removido com sucesso!');
            queryClient.invalidateQueries(['routing-redirect', id]);
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao remover redirecionamento');
            toastError(message);
        },
    });

    const handleOpenCreate = useCallback(() => {
        setEditing(null);
        setFieldErrors({});
        setModalOpen(true);
    }, []);

    const handleOpenEdit = useCallback((row: RoutingRedirectDTO) => {
        setEditing(row);
        setFieldErrors({});
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setEditing(null);
        setFieldErrors({});
    }, []);

    const handleSave = useCallback((
        data: Omit<RoutingRedirectDTO, 'id' | 'wan_nome' | 'data_criacao' | 'data_ultima_atualizacao'>
    ) => {
        if (editing?.id) {
            mutUpdate.mutate({ redirectId: editing.id, data });
        } else {
            mutCreate.mutate(data);
        }
    }, [editing, mutCreate, mutUpdate]);

    const appliance = applianceQuery.data;
    const redirects = redirectsQuery.data ?? [];

    const resolveWanName = (row: RoutingRedirectDTO): string => {
        if (!appliance) return String(row.wan_id);
        const w = appliance.redesWan?.find(x => String(x.id) === String(row.wan_id));
        return w ? w.nome : String(row.wan_id);
    };

    const headers: DataTableHeader[] = [
        {
            column: 'order',
            label: 'Ordem',
            render: (row: RoutingRedirectDTO) => row.order,
        },
        {
            column: 'nome',
            label: 'Nome',
            render: (row: RoutingRedirectDTO) => row.nome,
        },
        {
            column: 'destinos',
            label: 'Destinos',
            render: (row: RoutingRedirectDTO) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {row.destinos.map(d => <span key={d}>{d}</span>)}
                </div>
            ),
        },
        {
            column: 'wan',
            label: 'Link WAN',
            render: (row: RoutingRedirectDTO) => resolveWanName(row),
        },
        {
            column: 'enabled',
            label: 'Status',
            render: (row: RoutingRedirectDTO) => row.enabled ? '✅ Habilitado' : '⏸ Desabilitado',
        },
        {
            column: 'comment',
            label: 'Comentário',
            render: (row: RoutingRedirectDTO) => row.comment || '—',
        },
        {
            column: 'acoes',
            label: 'Ações',
            render: (row: RoutingRedirectDTO) => (
                <HBox style={{ gap: '8px' }}>
                    <Botao
                        size='sm'
                        variant="outline-primary"
                        icon={mdiPencil}
                        onClick={() => handleOpenEdit(row)}
                        title="Editar"
                    />
                    <BotaoPerigoso
                        size='sm'
                        variant="outline-danger"
                        icon={mdiTrashCan}
                        onConfirm={() => mutDelete.mutate(row.id!)}
                        title="Remover"
                    />
                </HBox>
            ),
        },
    ];

    const isLoading = applianceQuery.isLoading || redirectsQuery.isLoading;
    const isSaving = mutCreate.isLoading || mutUpdate.isLoading;

    return (
        <LayoutUsuario>
            <VBox style={{ padding: '24px', gap: '16px', height: '100%', overflow: 'hidden' }}>
                <HBox>
                    {paramId && (
                        <Botao
                            size="sm"
                            variant="outline-secondary"
                            icon={mdiArrowLeft}
                            onClick={() => navigate('/appliances')}
                            style={{ marginRight: '16px' }}
                        >
                            Voltar
                        </Botao>
                    )}
                    <VBox style={{ flex: 1 }}>
                        <Titulo>
                            Redirecionamento de tráfego
                            {appliance ? ` — ${appliance.nome}` : ''}
                        </Titulo>
                        <Descricao>
                            Fazer o tráfego para certos domínios, IPs ou faixas (CIDR) sair por um link WAN específico.
                            As regras são aplicadas ao sincronizar o appliance.
                        </Descricao>
                    </VBox>
                    <Botao
                        icon={mdiPlus}
                        onClick={handleOpenCreate}
                        variant="primary"
                        size="md"
                        disabled={!appliance}
                    >
                        Adicionar
                    </Botao>
                </HBox>

                {isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <Icon path={mdiLoading} spin size={2} />
                    </HBox>
                )}

                {!isLoading && (
                    <DataTable
                        cabecalhos={headers}
                        linhas={redirects}
                    />
                )}

                {!isLoading && redirects.length === 0 && (
                    <HBox style={{ justifyContent: 'center', padding: '24px' }}>
                        <Descricao>Nenhum redirecionamento configurado. Clique em "Adicionar" para criar o primeiro.</Descricao>
                    </HBox>
                )}
            </VBox>

            {modalOpen && appliance && (
                <RoutingRedirectModal
                    appliance={appliance}
                    redirect={editing}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                    saving={isSaving}
                    fieldErrors={fieldErrors}
                />
            )}
        </LayoutUsuario>
    );
};

export default Page;
